import React, { useMemo, useCallback } from 'react';
import './product-card.scss';
import {
  AddLineItems,
  CatalogueView,
  dispatch,
  getCatalogConfig,
  ProductCardVariant,
  ProductVariant,
  removeSelectedProducts,
  UOM,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { ProductImage } from '../../components';
import { CategoryTags } from '../category-tags';
import { OrderProductActionType } from '../../constants';
import { PriceUpdatedProduct, ProductPriceUpdateMap } from '../../types';
import { OrderActions } from './order-actions';
import { StockToggle } from './stock-toggle';
import { EditPriceFields } from './edit-price-fields';
import { ProductMeta } from './product-meta';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { ShareProductCheckbox } from './share-product-checkbox';
import { ReactComponent as DeleteIcon } from '@zonofi/svg-icons/delete.svg';
import { PromotionsList } from './promotions-list';
import { ProductFacets } from '../product-facets/product-facets';
import { ProductSegments } from '../product-segments/product-segments';
import { getProductVariantStock } from '../../helper/parse-inventory-stock';

export interface ProductCardProps {
  variant: ProductCardVariant;
  product: ProductVariant;
  onAddProductPress?: (productVariant: ProductVariant, uom?: UOM) => void;
  onReplaceProductPress?: (productVariant: ProductVariant) => void;
  onProductRemoveAndUpdate?: (
    productVariant: ProductVariant,
    type?: OrderProductActionType
  ) => void;
  toggleProductAvaiablity?: (val: ProductVariant) => Promise<boolean>;
  updatedProducts?: ProductPriceUpdateMap;
  onUpdatePrice?: (item: PriceUpdatedProduct) => void;
  selectedProducts?: Set<number>;
  onSelectProduct?: (val: number) => void;
  lineItems?: AddLineItems[];
  isByCustomer?: boolean;
  openUomModal?: () => void;
  currentProductId?: number;
}

// Constants for better maintainability
const ORDER_FLOW_VARIANTS = [
  ProductCardVariant.ORDER_ADD,
  ProductCardVariant.ORDER_REPLACE,
  ProductCardVariant.ORDER_MAP,
];

const PRODUCT_LISTING_VARIANTS = [
  ProductCardVariant.STOCK,
  ProductCardVariant.SHARE_PRODUCT,
  ProductCardVariant.VIEW_PRODUCT,
];

export const ProductCard: React.FC<ProductCardProps> = ({
  variant,
  product,
  onAddProductPress,
  onReplaceProductPress,
  onProductRemoveAndUpdate,
  toggleProductAvaiablity,
  updatedProducts,
  onUpdatePrice,
  selectedProducts,
  onSelectProduct,
  lineItems,
  isByCustomer = false,
  openUomModal,
  currentProductId,
}) => {
  const catalogueConfig = useAppSelector(getCatalogConfig);
  const { showOutOfStockTag } = catalogueConfig;

  // Memoized computed values
  const showImage = useMemo(
    () => catalogueConfig.catalogueView === CatalogueView.ListWithImagesView,
    [catalogueConfig.catalogueView]
  );

  const imageUrl = useMemo(
    () => product?.assets?.[0]?.redirectUrl,
    [product?.assets]
  );

  const currentProduct = useMemo(
    () => updatedProducts?.[product.id.toString()],
    [updatedProducts, product.id]
  );

  const initialPrice = useMemo(() => {
    const defaultPrice = isByCustomer ? product?.basePrice : product?.price;

    return currentProduct?.price ?? defaultPrice ?? 0;
  }, [currentProduct?.price, product?.basePrice, product?.price, isByCustomer]);

  const isOrderFlow = useMemo(
    () => ORDER_FLOW_VARIANTS.includes(variant),
    [variant]
  );

  const isOutOfStock = useMemo(() => {
    if (!isOrderFlow || !showOutOfStockTag) return false;
    const stock = getProductVariantStock(product);
    return typeof stock === 'number' && stock < 1;
  }, [isOrderFlow, product, showOutOfStockTag]);

  const isProductListing = useMemo(
    () => PRODUCT_LISTING_VARIANTS.includes(variant),
    [variant]
  );

  const isProductPricing = useMemo(
    () => isProductListing || variant === ProductCardVariant.EDIT_PRICE,
    [isProductListing, variant]
  );

  const handleDeleteProduct = useCallback(() => {
    dispatch(removeSelectedProducts(product.id));
  }, [product.id]);

  // Simple render functions (keep these as they're short and tightly coupled)
  const renderOrderFlowContent = () => {
    if (!isOrderFlow) return null;

    return (
      <>
        <PromotionsList product={product} />
        <ProductMeta variant={variant} product={product} price={initialPrice} />
      </>
    );
  };

  const renderPriceDisplay = () => {
    if (!isProductListing) return null;

    return (
      <span className="stock-price font-semibold">
        Price:{' '}
        <span className="stock-price-amount">
          {getFormattedAmount(Number(initialPrice))}
        </span>
      </span>
    );
  };

  const renderVariantSpecificContent = () => {
    switch (variant) {
      case ProductCardVariant.EDIT_PRICE:
        return (
          <div className="product-card-variants-view">
            <div className="product-card-variants-view--divider" />
            <EditPriceFields
              product={product}
              updatedProducts={updatedProducts}
              onUpdatePrice={onUpdatePrice}
            />
          </div>
        );

      case ProductCardVariant.STOCK:
        return (
          <div className="product-card-variants-view">
            <div className="product-card-variants-view--divider" />
            <StockToggle
              product={product}
              toggleProductAvaiablity={toggleProductAvaiablity}
            />
          </div>
        );

      case ProductCardVariant.SHARE_PRODUCT:
        return (
          <div className="product-card-variants-view">
            <div className="product-card-variants-view--divider" />
            <ShareProductCheckbox
              product={product}
              selectedProducts={selectedProducts!}
              onSelectProduct={onSelectProduct!}
            />
          </div>
        );

      case ProductCardVariant.VIEW_PRODUCT:
        return (
          <div
            className="product-card-view-delete"
            onClick={handleDeleteProduct}
          >
            <DeleteIcon color="#DD360C" />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="product-card">
      <div className={`product-card-view ${variant ? 'variant-view' : ''}`}>
        <div className="product-card-view-card">
          {showImage && <ProductImage productImage={imageUrl} />}

          <div>
            <div className="product-card-title-row">
              <div className="product-details-title font-medium">
                {product?.name}
              </div>
              {showOutOfStockTag && isOutOfStock && (
                <span className="product-card-out-of-stock font-semibold">
                  OUT OF STOCK
                </span>
              )}
            </div>

            {(product?.facets?.length > 0 ||
              (product?.segments ?? [])?.length > 0 ||
              product?.categories?.length > 0) && (
              <div className="product-meta-row">
                {product?.facets?.length > 0 && (
                  <ProductFacets
                    facets={product?.facets}
                    isProductPricing={isProductPricing}
                  />
                )}
                {product?.facets?.length > 0 &&
                  (product?.segments ?? [])?.length > 0 && (
                    <div className="product-meta-divider" />
                  )}
                {(product?.segments ?? [])?.length > 0 && (
                  <ProductSegments segments={product.segments ?? []} />
                )}
                {(product?.segments ?? [])?.length > 0 &&
                  product?.categories?.length > 0 && (
                    <div className="product-meta-divider" />
                  )}
                {product?.categories?.length > 0 && (
                  <CategoryTags categories={product.categories} />
                )}
              </div>
            )}

            {renderOrderFlowContent()}
            {renderPriceDisplay()}
          </div>
        </div>
        {renderVariantSpecificContent()}
      </div>
      {isOrderFlow && (
        <div className="product-edit-card">
          <OrderActions
            variant={variant}
            product={product}
            onAddProductPress={onAddProductPress}
            onReplaceProductPress={onReplaceProductPress}
            onProductRemoveAndUpdate={onProductRemoveAndUpdate}
            lineItems={lineItems}
            price={initialPrice}
            openUomModal={openUomModal}
            currentProductId={currentProductId}
            isOutOfStock={isOutOfStock}
          />
        </div>
      )}
    </div>
  );
};
